// @ts-nocheck
/*
 * Port of views/dashboard/notification/agent_notification_panel.js.
 * APIs: API_AGENTS_LIST, API_NOTIFICATION_LIST, API_NOTIFICATION_ASSIGN_TO_AGENT,
 *       API_NOTIFICATION_ASSIGNED_TO_AGENT.
 * Main table via obd3-gui DataTable with row selection for bulk-assign.
 * Row expansion in v1 (per-agent assigned notifications) is ported as
 * a "View assignments" button opening a per-agent modal — same data, same
 * counts, same behavior of the assign flow.
 * Main table columns preserved: Agent, Profile Login, Dashboard Login,
 * Assigned Notifications, Action (5, plus DataTable's built-in selection).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { getApiCallShared } from '@/service/sharedGet';
import {
  API_AGENTS_LIST,
  API_NOTIFICATION_LIST,
  API_NOTIFICATION_ASSIGN_TO_AGENT,
  API_NOTIFICATION_ASSIGNED_TO_AGENT,
} from '@/service/url';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';

function AssignNotificationsModal({ show, onHide, agents, preselectedAgentIds, onAssigned }) {
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationSearch, setNotificationSearch] = useState('');
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelectedAgentIds(preselectedAgentIds || []);
    setSelectedNotificationIds([]);
    setNotificationSearch('');
    setAgentSearch('');
    const loadNotifications = async () => {
      try {
        setLoadingNotifications(true);
        const resp = await getApiCall(API_NOTIFICATION_LIST);
        const data = resp?.data || resp;
        setNotifications(Array.isArray(data) ? data.filter((n) => !n.is_deleted) : []);
      } catch (e) {
        toast.error(e || 'Failed to load notifications');
      } finally { setLoadingNotifications(false); }
    };
    loadNotifications();
  }, [show, preselectedAgentIds]);

  const filteredNotifications = notificationSearch.trim()
    ? notifications.filter((n) => `${n.notification_type} ${n.description}`.toLowerCase().includes(notificationSearch.toLowerCase()))
    : notifications;

  const filteredAgents = agentSearch.trim()
    ? agents.filter((a) => `${a.agent_name} ${a.username} ${a.agent_number}`.toLowerCase().includes(agentSearch.toLowerCase()))
    : agents;

  const toggleNotification = (id) =>
    setSelectedNotificationIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAgent = (id) =>
    setSelectedAgentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const handleSelectAllAgents = (checked) => setSelectedAgentIds(checked ? filteredAgents.map((a) => a.id) : []);

  const submitAssign = async () => {
    if (selectedNotificationIds.length === 0) return toast.warning('Select at least one notification');
    if (selectedAgentIds.length === 0) return toast.warning('Select at least one agent');
    try {
      setSubmitting(true);
      await postApiCall(API_NOTIFICATION_ASSIGN_TO_AGENT, {
        notification_ids: selectedNotificationIds.map((id) => String(id)),
        agent_ids: selectedAgentIds.map((id) => String(id)),
      });
      toast.success('Notifications assigned');
      onAssigned && onAssigned();
      onHide();
    } catch (e) {
      toast.error(e || 'Failed to assign notifications');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Assign Notifications to Agent(s)</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-bold">Notifications</label>
            <Input placeholder="Search notifications..." value={notificationSearch} onChange={(e) => setNotificationSearch(e.target.value)} className="mb-2 h-9" />
            <div className="max-h-[300px] overflow-auto rounded-md border border-border p-2">
              {loadingNotifications ? (
                <div className="text-sm text-muted-foreground">Loading notifications...</div>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-sm text-muted-foreground">No notifications found</div>
              ) : (
                filteredNotifications.map((n) => (
                  <label key={n.id} className="flex cursor-pointer items-center gap-2 py-1">
                    <Checkbox checked={selectedNotificationIds.includes(n.id)} onCheckedChange={() => toggleNotification(n.id)} />
                    <div>
                      <div className="text-sm">{n.notification_type}</div>
                      {n.description && <div className="text-xs text-muted-foreground">{n.description}</div>}
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-sm font-bold">Target Agents</label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                <Checkbox
                  checked={filteredAgents.length > 0 && selectedAgentIds.length === filteredAgents.length}
                  onCheckedChange={handleSelectAllAgents}
                />
                Select all
              </label>
            </div>
            <Input placeholder="Search agents..." value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} className="mb-2 h-9" />
            <div className="max-h-[300px] overflow-auto rounded-md border border-border p-2">
              {filteredAgents.length === 0 ? (
                <div className="text-sm text-muted-foreground">No agents found</div>
              ) : (
                filteredAgents.map((a) => (
                  <label key={a.id} className="flex cursor-pointer items-center gap-2 py-1">
                    <Checkbox checked={selectedAgentIds.includes(a.id)} onCheckedChange={() => toggleAgent(a.id)} />
                    <div>
                      <div className="text-sm">{a.agent_name}</div>
                      <div className="text-xs text-muted-foreground">{[a.username, a.agent_number].filter(Boolean).join(' - ')}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onHide}>Cancel</Button>
          <Button onClick={submitAssign} disabled={submitting}>{submitting ? 'Assigning...' : 'Assign'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AgentNotificationPanel() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [modalPreselected, setModalPreselected] = useState([]);
  const [viewAgentId, setViewAgentId] = useState(null);

  const normalizeAgents = (raw) =>
    (Array.isArray(raw) ? raw : []).map((a) => ({
      id: a.id,
      agent_name: a.agent_name || a.username || `Agent #${a.id}`,
      username: a.username || a.linkedUser?.username || '',
      agent_number: a.agent_number || '',
      login_status: a.login_status,
      dashboardLoggedIn: a.linkedUser?.login_at !== null && a.linkedUser?.login_at !== undefined,
    }));

  const fetchAgents = async (silent = false, shared = false) => {
    try {
      if (!silent) setLoading(true);
      const resp = await (shared ? getApiCallShared(API_AGENTS_LIST) : getApiCall(API_AGENTS_LIST));
      const data = resp?.data || resp;
      setAgents(normalizeAgents(data));
    } catch (e) {
      if (!silent) toast.error(e || 'Failed to load agents');
    } finally { if (!silent) setLoading(false); }
  };
  const fetchAssignments = async () => {
    try {
      const resp = await getApiCall(API_NOTIFICATION_ASSIGNED_TO_AGENT, {});
      const data = resp?.data || resp;
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) { setAssignments([]); }
  };
  const refreshAll = (silent = false, shared = false) => { fetchAgents(silent, shared); fetchAssignments(); };

  const refreshAllRef = useRef(refreshAll);
  refreshAllRef.current = refreshAll;

  useEffect(() => {
    refreshAll(false, true);
    const onRefresh = () => refreshAllRef.current?.(true);
    window.addEventListener('cti:notification_refresh', onRefresh);
    const interval = setInterval(() => { if (!document.hidden) refreshAllRef.current?.(true); }, 120000);
    return () => {
      window.removeEventListener('cti:notification_refresh', onRefresh);
      clearInterval(interval);
    };
  }, []);

  const assignmentsByAgent = useMemo(() => {
    const map = new Map();
    assignments.forEach((row) => {
      const agentId = row.agent_id ?? row.agent?.id;
      if (agentId === undefined || agentId === null) return;
      if (!map.has(agentId)) map.set(agentId, []);
      map.get(agentId).push(row);
    });
    return map;
  }, [assignments]);

  const openAssignForRow = (agentId) => {
    setModalPreselected([agentId]);
    setShowAssignModal(true);
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'agent_name',
        header: 'Agent',
        cell: ({ row }) => (
          <div>
            <div>{row.original.agent_name}</div>
            <div className="text-xs text-muted-foreground">
              {[row.original.username, row.original.agent_number].filter(Boolean).join(' - ')}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'login_status',
        header: 'Profile Login',
        cell: ({ row }) => {
          const active = String(row.original.login_status) === '1';
          return (
            <span className={
              'rounded-full px-2 py-0.5 text-[11px] font-bold ' +
              (active ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground')
            }>
              {active ? 'Logged In' : 'Logged Out'}
            </span>
          );
        },
      },
      {
        accessorKey: 'dashboardLoggedIn',
        header: 'Dashboard Login',
        cell: ({ row }) => (
          <span className={
            'rounded-full px-2 py-0.5 text-[11px] font-bold ' +
            (row.original.dashboardLoggedIn ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground')
          }>
            {row.original.dashboardLoggedIn ? 'Logged In' : 'Not Logged In'}
          </span>
        ),
      },
      {
        id: 'assigned',
        header: 'Assigned Notifications',
        cell: ({ row }) => {
          const list = assignmentsByAgent.get(row.original.id) || [];
          const total = list.length;
          const read = list.filter((n) => n.is_read).length;
          if (total === 0) return <span className="text-muted-foreground">None</span>;
          return (
            <span>
              {total} assigned · <span className="text-emerald-600">{read} read</span> / <span className="text-red-500">{total - read} unread</span>
            </span>
          );
        },
      },
      {
        id: 'view',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setViewAgentId(row.original.id); }}>
            View
          </Button>
        ),
      },
      {
        id: 'assign',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openAssignForRow(row.original.id); }}>Assign</Button>
        ),
      },
    ],
    [assignmentsByAgent],
  );

  const viewAgent = useMemo(() => agents.find((a) => a.id === viewAgentId) || null, [agents, viewAgentId]);
  const viewAssignments = viewAgentId ? (assignmentsByAgent.get(viewAgentId) || []) : [];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-foreground">Agent Notifications</h4>
        <Button size="sm" variant="outline" onClick={() => refreshAll()}>Refresh</Button>
      </div>

      <DataTable
        tableId="dashboard.agent-notifications"
        columns={columns}
        data={agents}
        isLoading={loading}
        enableRowSelection
        renderSelectionActions={(selectedIds, clear) => (
          <Button
            size="sm"
            onClick={() => {
              const ids = selectedIds.map((s) => agents[parseInt(s, 10)]?.id).filter(Boolean);
              if (ids.length === 0) return toast.warning('Select at least one agent');
              setModalPreselected(ids);
              setShowAssignModal(true);
              clear();
            }}
          >
            Assign Notifications ({selectedIds.length})
          </Button>
        )}
        emptyTitle="No agents"
        emptyDescription="No agents found."
        pageSize={10}
        exportFilename="agent-notifications.csv"
      />

      <Dialog open={!!viewAgentId} onOpenChange={(o) => !o && setViewAgentId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Notifications — {viewAgent?.agent_name}</DialogTitle></DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {viewAssignments.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No notifications assigned.</div>
            ) : (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <th className="px-2 py-1.5 font-bold">Notification</th>
                    <th className="px-2 py-1.5 font-bold">Description</th>
                    <th className="px-2 py-1.5 font-bold">Read</th>
                    <th className="px-2 py-1.5 font-bold">Assigned At</th>
                  </tr>
                </thead>
                <tbody>
                  {viewAssignments.map((row) => (
                    <tr key={row.id} className="border-t border-border">
                      <td className="px-2 py-1.5">{row?.notification?.notification_type || row?.notification_type || row?.notification_id || '-'}</td>
                      <td className="px-2 py-1.5">{row?.notification?.description || '-'}</td>
                      <td className="px-2 py-1.5">
                        <span className={
                          'rounded-full px-2 py-0.5 text-[11px] font-bold ' +
                          (row.is_read ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700')
                        }>
                          {row.is_read ? 'Read' : 'Unread'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{row?.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AssignNotificationsModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        agents={agents}
        preselectedAgentIds={modalPreselected}
        onAssigned={() => refreshAll()}
      />
    </>
  );
}
